import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  InputNumber,
  Modal,
  Row,
  Space,
  Statistic,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  BankOutlined,
  ClockCircleOutlined,
  EditOutlined,
  GlobalOutlined,
  ReloadOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/ru';
import {
  currencyApi,
  CurrencyBank,
  CurrencyUpdatePayload,
} from '../../api/currency';

dayjs.extend(relativeTime);
dayjs.locale('ru');

const { Title, Text } = Typography;

const formatRate = (buy: number | null, sell: number | null) => {
  if (buy == null && sell == null) return <Text type="secondary">—</Text>;
  const fmt = (v: number | null) =>
    v == null ? '—' : v.toFixed(2).replace(/\.00$/, '');
  return (
    <Space size={4}>
      <Text strong>{fmt(buy)}</Text>
      <Text type="secondary">·</Text>
      <Text strong style={{ color: '#0F766E' }}>
        {fmt(sell)}
      </Text>
    </Space>
  );
};

const CurrencyPage: React.FC = () => {
  const [banks, setBanks] = useState<CurrencyBank[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<CurrencyBank | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  const [messageApi, contextHolder] = message.useMessage();

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await currencyApi.list();
      const list = Array.isArray(res.data) ? [...res.data] : [];
      list.sort((a, b) => a.sort_order - b.sort_order);
      setBanks(list);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Ошибка загрузки курсов');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const res = await currencyApi.refresh();
      const { updated = 0, failed = 0 } = res.data?.result || {};
      messageApi.success(`Обновлено: ${updated}, не удалось: ${failed}`);
      if (Array.isArray(res.data?.banks)) {
        const list = [...res.data.banks];
        list.sort((a, b) => a.sort_order - b.sort_order);
        setBanks(list);
      } else {
        fetchData();
      }
    } catch (err: any) {
      messageApi.error(err?.response?.data?.detail || 'Ошибка обновления');
    } finally {
      setRefreshing(false);
    }
  };

  const openEdit = (bank: CurrencyBank) => {
    setEditing(bank);
    form.setFieldsValue({
      usd_buy: bank.usd_buy,
      usd_sell: bank.usd_sell,
      eur_buy: bank.eur_buy,
      eur_sell: bank.eur_sell,
      rub_buy: bank.rub_buy,
      rub_sell: bank.rub_sell,
      is_active: bank.is_active,
      sort_order: bank.sort_order,
    });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    if (!editing) return;
    let values: CurrencyUpdatePayload;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setSubmitting(true);
    try {
      await currencyApi.update(editing.id, values);
      messageApi.success('Курсы обновлены');
      setModalOpen(false);
      fetchData();
    } catch (err: any) {
      messageApi.error(err?.response?.data?.detail || 'Ошибка сохранения');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (bank: CurrencyBank, value: boolean) => {
    try {
      await currencyApi.update(bank.id, { is_active: value });
      setBanks((prev) =>
        prev.map((b) => (b.id === bank.id ? { ...b, is_active: value } : b)),
      );
      messageApi.success(value ? 'Банк активирован' : 'Банк отключён');
    } catch (err: any) {
      messageApi.error(err?.response?.data?.detail || 'Ошибка');
    }
  };

  const activeBanks = useMemo(
    () => banks.filter((b) => b.is_active).length,
    [banks],
  );

  const lastUpdated = useMemo(() => {
    const timestamps = banks
      .map((b) => (b.updated_at ? dayjs(b.updated_at).valueOf() : 0))
      .filter((t) => t > 0);
    if (timestamps.length === 0) return null;
    return dayjs(Math.max(...timestamps));
  }, [banks]);

  const columns: ColumnsType<CurrencyBank> = [
    {
      title: 'Банк',
      key: 'bank',
      render: (_: any, r: CurrencyBank) => (
        <Space direction="vertical" size={2}>
          <Tag style={{ fontWeight: 700, color: 'var(--l-ink)' }}>
            {r.bank_short}
          </Tag>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {r.bank_name}
          </Text>
        </Space>
      ),
    },
    {
      title: 'USD (покупка · продажа)',
      key: 'usd',
      width: 200,
      render: (_: any, r: CurrencyBank) => formatRate(r.usd_buy, r.usd_sell),
    },
    {
      title: 'EUR (покупка · продажа)',
      key: 'eur',
      width: 200,
      render: (_: any, r: CurrencyBank) => formatRate(r.eur_buy, r.eur_sell),
    },
    {
      title: 'RUB (покупка · продажа)',
      key: 'rub',
      width: 200,
      render: (_: any, r: CurrencyBank) => formatRate(r.rub_buy, r.rub_sell),
    },
    {
      title: 'Обновлено',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 160,
      render: (v: string | null) =>
        v ? (
          <Tooltip title={dayjs(v).format('DD.MM.YYYY HH:mm:ss')}>
            <Space size={4}>
              <ClockCircleOutlined style={{ color: 'var(--l-faint)' }} />
              <Text type="secondary">{dayjs(v).fromNow()}</Text>
            </Space>
          </Tooltip>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: 'Активен',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 110,
      align: 'center',
      render: (v: boolean, record: CurrencyBank) => (
        <Switch
          checked={v}
          onChange={(val) => handleToggleActive(record, val)}
        />
      ),
    },
    {
      title: 'Порядок',
      dataIndex: 'sort_order',
      key: 'sort_order',
      width: 90,
      align: 'center',
      render: (v: number) => <Tag>{v}</Tag>,
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 90,
      align: 'center',
      render: (_: any, record: CurrencyBank) => (
        <Tooltip title="Редактировать">
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => openEdit(record)}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <div>
      {contextHolder}

      <div
        style={{
          marginBottom: 24,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 16,
        }}
      >
        <div>
          <Title level={3} style={{ margin: 0, color: 'var(--l-ink)' }}>
            Курсы валют
          </Title>
          <Text type="secondary">
            Автоматический парсинг iFin.kz каждый час
          </Text>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={fetchData} loading={loading}>
            Перечитать
          </Button>
          <Button
            type="primary"
           
            icon={<SyncOutlined spin={refreshing} />}
            onClick={handleRefresh}
            loading={refreshing}
            style={{ background: 'var(--l-accent)' }}
          >
            Обновить сейчас
          </Button>
        </Space>
      </div>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Активных банков"
              value={activeBanks}
              suffix={`/ ${banks.length}`}
              prefix={<BankOutlined style={{ color: 'var(--l-accent)' }} />}
              valueStyle={{ color: 'var(--l-ink)' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Последнее обновление"
              value={lastUpdated ? lastUpdated.fromNow() : '—'}
              prefix={<ClockCircleOutlined style={{ color: 'var(--l-accent)' }} />}
              valueStyle={{ fontSize: 20 }}
            />
            {lastUpdated && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {lastUpdated.format('DD.MM.YYYY HH:mm')}
              </Text>
            )}
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Источник"
              value="iFin.kz"
              prefix={<GlobalOutlined style={{ color: 'var(--l-warn)' }} />}
              valueStyle={{ fontSize: 20 }}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Парсер Playwright, раз в час
            </Text>
          </Card>
        </Col>
      </Row>

      {error && (
        <Alert
          type="error"
          message={error}
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      )}

      <Card bodyStyle={{ padding: 0 }}>
        <Table<CurrencyBank>
          rowKey="id"
          columns={columns}
          dataSource={banks}
          loading={loading}
          pagination={false}
          locale={{ emptyText: 'Банки не найдены' }}
          scroll={{ x: 1000 }}
        />
      </Card>

      <Modal
        title={editing ? `Редактировать — ${editing.bank_short}` : 'Редактировать банк'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSubmit}
        confirmLoading={submitting}
        okText="Сохранить"
        cancelText="Отмена"
        width={620}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="usd_buy" label="USD — покупка">
                <InputNumber
                  style={{ width: '100%' }}
                  step={0.01}
                  precision={2}
                  placeholder="0.00"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="usd_sell" label="USD — продажа">
                <InputNumber
                  style={{ width: '100%' }}
                  step={0.01}
                  precision={2}
                  placeholder="0.00"
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="eur_buy" label="EUR — покупка">
                <InputNumber
                  style={{ width: '100%' }}
                  step={0.01}
                  precision={2}
                  placeholder="0.00"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="eur_sell" label="EUR — продажа">
                <InputNumber
                  style={{ width: '100%' }}
                  step={0.01}
                  precision={2}
                  placeholder="0.00"
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="rub_buy" label="RUB — покупка">
                <InputNumber
                  style={{ width: '100%' }}
                  step={0.01}
                  precision={2}
                  placeholder="0.00"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="rub_sell" label="RUB — продажа">
                <InputNumber
                  style={{ width: '100%' }}
                  step={0.01}
                  precision={2}
                  placeholder="0.00"
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="sort_order"
                label="Порядок сортировки"
                tooltip="Меньше = выше в списке"
              >
                <InputNumber style={{ width: '100%' }} step={1} precision={0} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="is_active"
                label="Активен"
                valuePropName="checked"
              >
                <Switch checkedChildren="Да" unCheckedChildren="Нет" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default CurrencyPage;
